import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useApp } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const getUrgencyColor = (minutes) => {
  if (minutes <= 15) return 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20';
  if (minutes <= 30) return 'text-amber-400 bg-amber-400/10 border-amber-400/20';
  return 'text-rose-400 bg-rose-400/10 border-rose-400/20';
};

export default function Home() {
  const { reports, user } = useApp();
  const navigate = useNavigate();
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(interval);
  }, []);

  const terminalAWait = Math.round(reports.filter(r => r.terminal === 'Terminal A').reduce((acc, r) => acc + r.waitTime, 0) / (reports.filter(r => r.terminal === 'Terminal A').length || 1));
  const terminalBWait = Math.round(reports.filter(r => r.terminal === 'Terminal B').reduce((acc, r) => acc + r.waitTime, 0) / (reports.filter(r => r.terminal === 'Terminal B').length || 1));

  return (
    <div className="p-4 space-y-6 pb-24 md:pb-8">
      {/* Dynamic CTA for Guests */}
      {!user.isLoggedIn && (
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-br from-amber-500 to-orange-600 p-5 rounded-2xl shadow-lg relative overflow-hidden group cursor-pointer"
          onClick={() => navigate('/profile')}
        >
          <div className="relative z-10">
            <h3 className="text-slate-900 font-extrabold text-lg leading-tight">Join the Traveler Network</h3>
            <p className="text-slate-900/80 text-xs font-medium mt-1 pr-12">
              Sign up to unlock rewards and the <span className="font-bold">Travel Newsletter</span>.
            </p>
            <div className="mt-4 flex items-center text-slate-900 text-xs font-bold uppercase tracking-wider">
              <span>Get Started</span>
              <SafeIcon icon={FiIcons.FiArrowRight} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
          <SafeIcon icon={FiIcons.FiMail} className="absolute -right-4 -bottom-4 text-8xl text-slate-900/10 -rotate-12" />
        </motion.div>
      )}

      {/* Terminal Summary Cards */}
      <div className="grid grid-cols-2 gap-3 mb-2">
        <div className="bg-slate-850 border border-slate-800 p-4 rounded-2xl">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Terminal A Avg</p>
          <div className="flex items-baseline space-x-1">
            <span className={`text-2xl font-mono font-bold ${getUrgencyColor(terminalAWait).split(' ')[0]}`}>{terminalAWait}</span>
            <span className="text-xs text-slate-500">min</span>
          </div>
        </div>
        <div className="bg-slate-850 border border-slate-800 p-4 rounded-2xl">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Terminal B Avg</p>
          <div className="flex items-baseline space-x-1">
            <span className={`text-2xl font-mono font-bold ${getUrgencyColor(terminalBWait).split(' ')[0]}`}>{terminalBWait}</span>
            <span className="text-xs text-slate-500">min</span>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-white mb-1">Live Wait Times</h2>
          <p className="text-slate-400 text-sm">Real-time crowdsourced data for SMF</p>
        </div>
      </div>

      <div className="space-y-3">
        {reports.map((report, idx) => (
          <motion.div 
            key={report.id} 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="bg-slate-850 rounded-2xl border border-slate-800 overflow-hidden shadow-lg relative"
          >
            {report.isMock && (
              <div className="absolute top-0 right-0 px-2 py-1 bg-blue-500/10 text-blue-400 text-[8px] font-black uppercase tracking-widest border-b border-l border-blue-500/20 rounded-bl-lg">
                Verified Example
              </div>
            )}
            <div className="p-4 flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="font-bold text-white uppercase tracking-tight">{report.airline}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-slate-800 text-slate-400 font-bold uppercase">
                    {report.terminal}
                  </span>
                </div>
                <div className="flex items-center text-xs text-slate-500 space-x-4">
                  <span className="flex items-center font-medium">
                    <SafeIcon icon={FiIcons.FiShield} className="mr-1.5 text-blue-400" /> {report.checkpoint || 'Standard'}
                  </span>
                  <span className="flex items-center">
                    <SafeIcon icon={FiIcons.FiClock} className="mr-1.5" /> {formatDistanceToNow(new Date(report.timestamp), { addSuffix: true })}
                  </span>
                </div>
                {report.tip && (
                  <p className="text-[10px] text-emerald-400/80 mt-2 font-medium flex items-start">
                    <SafeIcon icon={FiIcons.FiInfo} className="mr-1.5 mt-0.5 shrink-0" />
                    <span>{report.tip}</span>
                  </p>
                )}
              </div>
              <div className={`ml-4 flex flex-col items-center justify-center w-20 h-20 rounded-2xl border ${getUrgencyColor(report.waitTime)}`}>
                <span className="text-3xl font-mono font-bold leading-none">{report.waitTime}</span>
                <span className="text-[10px] font-bold uppercase tracking-widest opacity-80 mt-1">Min</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}